import React, { useState, useEffect, ReactNode } from 'react';
import { BsArrowRight, BsArrowLeft, BsX } from 'react-icons/bs';

export interface WalkthroughStep {
  target: string; // CSS selector for the element to highlight
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  onActive?: () => void; // Function to call when this step becomes active
  onExit?: (userInput?: string) => void; // Function to call when navigating away from this step
  isInputStep?: boolean; // Whether this step includes a user input
  placeholder?: string; // Custom placeholder for input fields
  prefix?: string; // Text prefix to display before the input
  maxLength?: number; // Maximum character limit for input
}

interface WalkthroughProps {
  isOpen: boolean;
  onClose: () => void;
  steps: WalkthroughStep[];
  setupNotepad?: (prompt: string) => void; // Function to set up the notepad with todos
}

const Walkthrough: React.FC<WalkthroughProps> = ({ isOpen, onClose, steps, setupNotepad }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipArrowPosition, setTooltipArrowPosition] = useState<{ 
    top: string | number; 
    left: string | number;
    right?: string | number;
    bottom?: string | number;
  }>({ top: 0, left: 0 });
  const [tooltipArrowClass, setTooltipArrowClass] = useState('');
  
  // Use a map of step indexes to user inputs to keep track of separate inputs for each step
  const [userInputs, setUserInputs] = useState<{[key: number]: string}>({});
  
  // Constants for tooltip
  const TOOLTIP_WIDTH = 300;
  const TOOLTIP_HEIGHT = 200;
  const MARGIN = 20; // Minimum margin from the edge of the screen
  const ELEMENT_MARGIN = 15; // Margin between tooltip and target element
  
  // Reset inputs when walkthrough is opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setUserInputs({});
    }
  }, [isOpen]);
  
  // Get current step's input value
  const getCurrentStepInput = () => userInputs[currentStep] || '';
  
  // Update current step's input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInputs(prev => ({
      ...prev,
      [currentStep]: e.target.value
    }));
  };
  
  // Call the onActive function when a step becomes active
  useEffect(() => {
    if (isOpen && steps[currentStep]?.onActive) {
      steps[currentStep].onActive?.();
    }

    // Cleanup function to call onExit when leaving this step
    return () => {
      if (isOpen && steps[currentStep]?.onExit) {
        steps[currentStep].onExit?.();
      }
    };
  }, [isOpen, currentStep, steps]);
  
  // Position the tooltip based on the current step's target element
  useEffect(() => {
    if (!isOpen || !steps[currentStep]) return;
    
    let isMounted = true;
    
    // Delay positioning to ensure elements are fully rendered - reduced for better responsiveness
    const positionTimer = setTimeout(() => {
      if (!isMounted) return;
      
      const step = steps[currentStep];
      
      // For the tools panel, we need a slightly longer delay, but still keep it responsive
      const isToolsPanel = step.target.includes('tools-panel');
      
      // If this is for the tools panel but no element is found yet, wait a bit longer
      if (isToolsPanel && !document.querySelector(step.target)) {
        // Try again after another short delay
        const retryTimer = setTimeout(() => {
          if (!isMounted) return;
          
          const element = document.querySelector(step.target);
          if (element) {
            // Now the tools panel is visible, so position the tooltip
            positionTooltip(element, step);
          }
        }, 150); // Reduced delay for retry
        
        return () => {
          clearTimeout(retryTimer);
        };
      }
      
      const element = document.querySelector(step.target);
      if (element) {
        positionTooltip(element, step);
      }
    }, 150); // Reduced initial delay for better responsiveness
    
    // Helper function to position the tooltip
    function positionTooltip(element: Element, step: WalkthroughStep) {
      // Store the target element for rendering
      setTargetElement(element);
      
      // Get element's position and dimensions
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      
      // Calculate the best position considering screen edges and element size
      const position = step.position || findBestPosition(rect);
      
      // Calculate tooltip position
      const tooltipPos = calculateTooltipPosition(rect, position);
      setTooltipPosition(tooltipPos);
      
      // Calculate arrow position
      const arrowPos = calculateArrowPosition(position);
      setTooltipArrowPosition(arrowPos);
      
      // Set arrow class
      setTooltipArrowClass(`walkthrough-arrow-${position}`);
    }
    
    return () => {
      isMounted = false;
      clearTimeout(positionTimer);
    };
  }, [isOpen, currentStep, steps]);
  
  // Find the best position based on screen space
  const findBestPosition = (rect: DOMRect): 'top' | 'bottom' | 'left' | 'right' => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate available space in each direction
    const spaceTop = rect.top;
    const spaceBottom = viewportHeight - (rect.top + rect.height);
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - (rect.left + rect.width);
    
    // Check if we have enough space for the tooltip in each direction
    const hasSpaceTop = spaceTop >= TOOLTIP_HEIGHT + ELEMENT_MARGIN;
    const hasSpaceBottom = spaceBottom >= TOOLTIP_HEIGHT + ELEMENT_MARGIN;
    const hasSpaceLeft = spaceLeft >= TOOLTIP_WIDTH + ELEMENT_MARGIN;
    const hasSpaceRight = spaceRight >= TOOLTIP_WIDTH + ELEMENT_MARGIN;
    
    // Element is in the top half of the screen - prefer bottom placement
    if (rect.top < viewportHeight / 2) {
      if (hasSpaceBottom) return 'bottom';
      if (hasSpaceRight) return 'right';
      if (hasSpaceLeft) return 'left';
      return 'top'; // Fallback
    }
    // Element is in the bottom half - prefer top placement
    else {
      if (hasSpaceTop) return 'top';
      if (hasSpaceRight) return 'right';
      if (hasSpaceLeft) return 'left';
      return 'bottom'; // Fallback
    }
  };
  
  // Calculate tooltip position based on target element and desired position
  const calculateTooltipPosition = (rect: DOMRect, position: string) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let top, left;
    
    switch (position) {
      case 'top':
        // Position above the element
        top = rect.top - TOOLTIP_HEIGHT - ELEMENT_MARGIN;
        left = rect.left + (rect.width / 2) - (TOOLTIP_WIDTH / 2);
        break;
      
      case 'bottom':
        // Position below the element
        top = rect.bottom + ELEMENT_MARGIN;
        left = rect.left + (rect.width / 2) - (TOOLTIP_WIDTH / 2);
        break;
      
      case 'left':
        // Position to the left of the element
        top = rect.top + (rect.height / 2) - (TOOLTIP_HEIGHT / 2);
        left = rect.left - TOOLTIP_WIDTH - ELEMENT_MARGIN;
        break;
      
      case 'right':
        // Position to the right of the element
        top = rect.top + (rect.height / 2) - (TOOLTIP_HEIGHT / 2);
        left = rect.right + ELEMENT_MARGIN;
        break;
      
      default:
        // Default to bottom if position is invalid
        top = rect.bottom + ELEMENT_MARGIN;
        left = rect.left + (rect.width / 2) - (TOOLTIP_WIDTH / 2);
    }
    
    // Ensure tooltip stays within viewport bounds
    top = Math.max(MARGIN, Math.min(viewportHeight - TOOLTIP_HEIGHT - MARGIN, top));
    left = Math.max(MARGIN, Math.min(viewportWidth - TOOLTIP_WIDTH - MARGIN, left));
    
    return { top, left };
  };
  
  // Calculate arrow position
  const calculateArrowPosition = (position: string) => {
    // Different arrow positions based on tooltip position
    switch (position) {
      case 'top':
        return {
          bottom: '-10px',
          left: '50%',
          top: 'auto',
          right: 'auto'
        };
      
      case 'bottom':
        return {
          top: '-10px',
          left: '50%',
          bottom: 'auto',
          right: 'auto'
        };
      
      case 'left':
        return {
          top: '50%',
          right: '-10px',
          bottom: 'auto',
          left: 'auto'
        };
      
      case 'right':
        return {
          top: '50%',
          left: '-10px',
          bottom: 'auto',
          right: 'auto'
        };
      
      default:
        return { top: '0', left: '0' };
    }
  };
  
  // Return early if not open or no steps
  if (!isOpen || !steps.length) return null;
  
  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const isInputStep = currentStepData.isInputStep;
  
  // Handle next and previous steps
  const handleNext = () => {
    const currentStepData = steps[currentStep];
    
    // Call onExit for the current step before moving to the next
    if (currentStepData.onExit) {
      // Pass userInput to onExit if this is an input step
      if (currentStepData.isInputStep) {
        currentStepData.onExit(userInputs[currentStep]);
      } else {
        currentStepData.onExit();
      }
    }
    
    // If this is the last step and it's an input step, setup the notepad before closing
    if (isLastStep && isInputStep && setupNotepad && userInputs[currentStep]) {
      // Generate a todo list prompt
      const prompt = `Create a simple todo list for ${userInputs[currentStep]}`;
      setupNotepad(prompt);
      
      // Close the walkthrough
      setTimeout(() => onClose(), 500);
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(current => current + 1);
    } else {
      onClose();
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(current => current - 1);
    }
  };
  
  return (
    <div className="walkthrough-overlay">
      {/* Highlight overlay - separate from the actual element */}
      {targetRect && (
        <div 
          className="walkthrough-highlight-overlay"
          style={{
            position: 'absolute',
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            pointerEvents: 'none'
          }}
        />
      )}
      
      {/* Tooltip */}
      {targetElement && (
        <div 
          className="walkthrough-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
        >
          <div className="walkthrough-header">
            <div className="walkthrough-counter">{currentStep + 1} of {steps.length}</div>
          </div>
          
          <div className="walkthrough-content">
            <h3>{currentStepData.title}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>{currentStepData.content}</p>
            
            {/* Input field for input steps */}
            {isInputStep && (
              <div className="walkthrough-input-container">
                {currentStepData.prefix && (
                  <div className="walkthrough-input-wrapper">
                    <span className="walkthrough-input-prefix">{currentStepData.prefix}</span>
                    <input
                      type="text"
                      className="walkthrough-input with-prefix"
                      value={getCurrentStepInput()}
                      onChange={handleInputChange}
                      placeholder={currentStepData.placeholder || "What are you working on today?"}
                      maxLength={currentStepData.maxLength}
                      autoFocus
                    />
                  </div>
                )}
                {!currentStepData.prefix && (
                  <input
                    type="text"
                    className="walkthrough-input"
                    value={getCurrentStepInput()}
                    onChange={handleInputChange}
                    placeholder={currentStepData.placeholder || "What are you working on today?"}
                    maxLength={currentStepData.maxLength}
                    autoFocus
                  />
                )}
              </div>
            )}
          </div>
          
          <div className="walkthrough-footer">
            <button 
              className="walkthrough-prev"
              onClick={handlePrevious}
              disabled={isFirstStep}
            >
              <BsArrowLeft /> Previous
            </button>
            
            <button 
              className="walkthrough-next"
              onClick={handleNext}
              disabled={isInputStep && !getCurrentStepInput().trim()}
            >
              {isLastStep ? 'Finish' : 'Next'} {!isLastStep && <BsArrowRight />}
            </button>
          </div>
          
          <div 
            className={`walkthrough-arrow ${tooltipArrowClass}`}
            style={{
              top: tooltipArrowPosition.top,
              left: tooltipArrowPosition.left,
              right: tooltipArrowPosition.right,
              bottom: tooltipArrowPosition.bottom
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Walkthrough; 